import { useSyncExternalStore } from 'react'
import { z } from 'zod'

const ActiveOrgSlugCookieName = 'joyful_active_org_slug'
const ActiveOrgSlugCookieSchema = z.string().min(1)
const ActiveOrgSlugCookieChangedEvent = 'joyful-active-org-slug-cookie-change'
const ActiveOrgSlugCookieMaxAgeSeconds = 60 * 60 * 24 * 365

export function getActiveOrgSlugCookie() {
  if (typeof document === 'undefined') {
    return null
  }

  const cookiePrefix = `${ActiveOrgSlugCookieName}=`
  const cookie = document.cookie.split('; ').find((cookiePart) => {
    return cookiePart.startsWith(cookiePrefix)
  })
  if (!cookie) {
    return null
  }

  const encodedValue = cookie.slice(cookiePrefix.length)
  try {
    const decodedValue = decodeURIComponent(encodedValue)
    return ActiveOrgSlugCookieSchema.safeParse(decodedValue).data ?? null
  } catch {
    return null
  }
}

export function setActiveOrgSlugCookie(orgSlug: string) {
  if (typeof document === 'undefined') {
    return
  }

  const cookieParts = [
    `${ActiveOrgSlugCookieName}=${encodeURIComponent(orgSlug)}`,
    'Path=/',
    `Max-Age=${ActiveOrgSlugCookieMaxAgeSeconds}`,
    'SameSite=Lax',
  ]
  const cookieDomain = getActiveOrgSlugCookieDomain()
  if (cookieDomain) {
    cookieParts.push(`Domain=${cookieDomain}`)
  }

  if (globalThis.location.protocol === 'https:') {
    cookieParts.push('Secure')
  }

  document.cookie = cookieParts.join('; ')
  globalThis.dispatchEvent(new Event(ActiveOrgSlugCookieChangedEvent))
}

function subscribeToActiveOrgSlugCookie(onChange: () => void) {
  globalThis.addEventListener(ActiveOrgSlugCookieChangedEvent, onChange)
  globalThis.addEventListener('focus', onChange)
  globalThis.addEventListener('visibilitychange', onChange)

  return () => {
    globalThis.removeEventListener(ActiveOrgSlugCookieChangedEvent, onChange)
    globalThis.removeEventListener('focus', onChange)
    globalThis.removeEventListener('visibilitychange', onChange)
  }
}

function getActiveOrgSlugCookieServerSnapshot() {
  return null
}

export function useActiveOrgSlugCookie() {
  return useSyncExternalStore(
    subscribeToActiveOrgSlugCookie,
    getActiveOrgSlugCookie,
    getActiveOrgSlugCookieServerSnapshot,
  )
}

function getActiveOrgSlugCookieDomain() {
  const hostname = globalThis.location.hostname
  if (
    hostname === 'joyfulhealth.com' ||
    hostname.endsWith('.joyfulhealth.com')
  ) {
    return '.joyfulhealth.com'
  }

  if (hostname === 'joyfulhealth.io' || hostname.endsWith('.joyfulhealth.io')) {
    return '.joyfulhealth.io'
  }

  return null
}
